/**
 * CustomerFacingDisplay - Shows current order to customer at POS
 * 
 * This display sits opposite the POS terminal and shows:
 * - Items as they're added to the order
 * - Running total
 * - Final amount when payment is processed
 * - "Thank You" screen after payment
 * - Idle screen with branding and opening hours
 * 
 * Syncs with POS via:
 * 1. localStorage events (for same-browser tabs)
 * 2. API polling (for separate devices on network)
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { ShoppingBag, Check, Clock } from 'lucide-react';
import { api } from '../../utils/apiClient';

// Bulaccino brand colors
const BRAND_BROWN = '#4A2C2A';
const BRAND_CREAM = '#F5F1E8';
const BRAND_GOLD = '#C9A227';

// Cafe opening hours
const OPENING_HOURS = [
  { day: 'Monday', hours: '6:00 AM - 4:00 PM' },
  { day: 'Tuesday', hours: '6:00 AM - 4:00 PM' },
  { day: 'Wednesday', hours: '6:00 AM - 4:00 PM' },
  { day: 'Thursday', hours: '6:00 AM - 4:00 PM' },
  { day: 'Friday', hours: '6:00 AM - 4:00 PM' },
  { day: 'Saturday', hours: '7:00 AM - 2:00 PM' },
  { day: 'Sunday', hours: '8:00 AM - 1:00 PM' },
];

// Format currency for Fiji
const formatCurrency = (amount) => {
  return `$${(amount || 0).toFixed(2)}`;
};

export default function CustomerFacingDisplay() {
  const [items, setItems] = useState([]);
  const [subtotal, setSubtotal] = useState(0);
  const [tax, setTax] = useState(0);
  const [total, setTotal] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [discountName, setDiscountName] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [orderStatus, setOrderStatus] = useState('idle'); // idle, ordering, paid, thankyou
  const [orderType, setOrderType] = useState('Takeaway');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [useApiSync, setUseApiSync] = useState(false);
  const stationId = 'pos-main';
  
  // Track previous data to prevent unnecessary re-renders (fixes flickering)
  const previousDataRef = useRef(null);

  // Apply data from any source (localStorage or API)
  // Uses deep comparison to prevent flickering from unnecessary re-renders
  const applyDisplayData = useCallback((data) => {
    if (!data) return;
    
    // Deep compare with previous data - if identical, skip ALL state updates
    const dataString = JSON.stringify(data);
    if (previousDataRef.current === dataString) {
      return; // Data unchanged, no re-render needed
    }
    previousDataRef.current = dataString;
    
    // Data has changed, update all state at once
    const newItems = data.items || [];
    const newSubtotal = data.subtotal || 0;
    const newTax = data.tax || 0;
    const newTotal = data.total || 0;
    const newDiscount = data.discount || data.discount_amount || 0;
    const newDiscountName = data.discount_name || '';
    const newCustomerName = data.customer_name || '';
    const newOrderType = data.order_type || 'Takeaway';
    
    // Determine new status
    let newOrderStatus = 'idle';
    const newStatus = data.status;
    if (newStatus === 'active') {
      newOrderStatus = 'ordering';
    } else if (newStatus === 'paid') {
      newOrderStatus = 'thankyou';
    } else if (newStatus === 'receipt_question') {
      newOrderStatus = 'receipt_question';
    } else if (newStatus === 'idle' || !newStatus) {
      if (!newItems || newItems.length === 0) {
        newOrderStatus = 'idle';
      }
    } else {
      newOrderStatus = newStatus;
    }
    
    // Batch all state updates
    setItems(newItems);
    setSubtotal(newSubtotal);
    setTax(newTax);
    setTotal(newTotal);
    setDiscount(newDiscount);
    setDiscountName(newDiscountName);
    setCustomerName(newCustomerName);
    setOrderType(newOrderType);
    setOrderStatus(newOrderStatus);
    setLastUpdated(new Date());
  }, []);

  // Poll API for updates (for separate device sync)
  useEffect(() => {
    const fetchFromApi = async () => {
      try {
        const res = await api.get(`/pos/customer-display/${stationId}`);
        if (res.data) {
          applyDisplayData(res.data);
          setUseApiSync(true);
        }
      } catch (err) {
        // API not available, fallback to localStorage only
        setUseApiSync(false);
      }
    };

    // Try API first, then use it for polling if available
    fetchFromApi();
    const apiInterval = setInterval(fetchFromApi, 500);

    return () => clearInterval(apiInterval);
  }, [applyDisplayData, stationId]);

  // Listen for POS updates via localStorage (for same-browser tabs)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'pos_customer_display') {
        try {
          const data = JSON.parse(e.newValue);
          if (data) {
            applyDisplayData(data);
          }
        } catch (err) {
          console.error('Error parsing customer display data:', err);
        }
      }
    };

    // Listen for storage events from POS
    window.addEventListener('storage', handleStorageChange);

    // Also poll localStorage for same-tab updates (fallback when API unavailable)
    const pollInterval = setInterval(() => {
      if (useApiSync) return; // Skip if API sync is working
      
      const data = localStorage.getItem('pos_customer_display');
      if (data) {
        try {
          const parsed = JSON.parse(data);
          if (parsed && parsed.timestamp !== lastUpdated?.getTime()) {
            applyDisplayData(parsed);
          }
        } catch (err) {
          // Ignore parsing errors
        }
      }
    }, 500);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(pollInterval);
    };
  }, [lastUpdated, applyDisplayData, useApiSync]);

  // Auto-reset to idle after thank you screen
  useEffect(() => {
    if (orderStatus === 'thankyou') {
      const timer = setTimeout(() => {
        setOrderStatus('idle');
        setItems([]);
        setSubtotal(0);
        setTax(0);
        setTotal(0);
        setDiscount(0);
        setDiscountName('');
        setCustomerName('');
      }, 5000); // Show thank you for 5 seconds
      return () => clearTimeout(timer);
    }
  }, [orderStatus]);

  // Get current day for highlighting hours
  const currentDay = new Date().toLocaleDateString('en-US', { weekday: 'long' });

  // IMPORTANT: Check special statuses FIRST before the idle/empty-items check
  // These screens intentionally have empty items[] but should still display
  
  // Receipt Question Screen - "Would you like a receipt?"
  if (orderStatus === 'receipt_question') {
    return (
      <div 
        className="min-h-screen flex flex-col"
        style={{ backgroundColor: BRAND_CREAM }}
        data-testid="customer-display-receipt-question"
      >
        {/* Header with Logo */}
        <header 
          className="py-6 px-8 flex items-center justify-center"
          style={{ backgroundColor: BRAND_BROWN }}
        >
          <img 
            src="/bulaccino-logo-cfd.png" 
            alt="Bulaccino Cafe" 
            className="h-20 w-auto object-contain"
          />
        </header>

        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center p-8">
          <div className="text-center space-y-8">
            {/* Receipt Icon */}
            <div className="flex justify-center">
              <div 
                className="w-32 h-32 rounded-full flex items-center justify-center shadow-2xl"
                style={{ backgroundColor: BRAND_GOLD }}
              >
                <svg 
                  className="w-16 h-16 text-white" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                  />
                </svg>
              </div>
            </div>

            {/* Question */}
            <div className="space-y-4">
              <h1 
                className="text-5xl font-bold"
                style={{ color: BRAND_BROWN }}
              >
                Receipt?
              </h1>
              <p 
                className="text-3xl"
                style={{ color: BRAND_BROWN, opacity: 0.9 }}
              >
                Would you like a copy of your receipt?
              </p>
              {total > 0 && (
                <p 
                  className="text-4xl font-bold mt-4"
                  style={{ color: BRAND_GOLD }}
                >
                  Total: ${total.toFixed(2)}
                </p>
              )}
            </div>

            {/* Hint */}
            <div 
              className="rounded-2xl p-6 mt-6 inline-block"
              style={{ backgroundColor: BRAND_BROWN + '10', border: `2px solid ${BRAND_BROWN}20` }}
            >
              <p 
                className="text-xl"
                style={{ color: BRAND_BROWN }}
              >
                Please let the cashier know
              </p>
              <div className="flex justify-center gap-8 mt-4">
                <span 
                  className="text-2xl font-bold px-6 py-2 rounded-full"
                  style={{ backgroundColor: '#22C55E', color: 'white' }}
                >
                  Yes
                </span>
                <span 
                  className="text-2xl font-bold px-6 py-2 rounded-full"
                  style={{ backgroundColor: '#EF4444', color: 'white' }}
                >
                  No
                </span>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer 
          className="py-4 px-8 text-center text-sm"
          style={{ backgroundColor: BRAND_BROWN, color: BRAND_CREAM }}
        >
          <p className="opacity-80">Bulaccino Cafe • Namaka, Nadi • bulaccinocafe.com</p>
        </footer>
      </div>
    );
  }

  // Thank You Screen - Branded with logo
  if (orderStatus === 'thankyou' || orderStatus === 'paid') {
    return (
      <div 
        className="min-h-screen flex flex-col"
        style={{ backgroundColor: BRAND_CREAM }}
        data-testid="customer-display-thank-you"
      >
        {/* Header with Logo */}
        <header 
          className="py-6 px-8 flex items-center justify-center"
          style={{ backgroundColor: BRAND_BROWN }}
        >
          <img 
            src="/bulaccino-logo-cfd.png" 
            alt="Bulaccino Cafe" 
            className="h-20 w-auto object-contain"
          />
        </header>

        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center p-8">
          <div className="text-center space-y-8">
            {/* Success Icon */}
            <div className="flex justify-center">
              <div 
                className="w-36 h-36 rounded-full flex items-center justify-center shadow-2xl animate-bounce"
                style={{ backgroundColor: '#22C55E' }}
              >
                <Check className="w-24 h-24 text-white" />
              </div>
            </div>

            {/* Thank You Message */}
            <div className="space-y-4">
              <h1 
                className="text-6xl font-bold"
                style={{ color: BRAND_BROWN }}
              >
                Vinaka!
              </h1>
              <p 
                className="text-3xl"
                style={{ color: BRAND_BROWN, opacity: 0.9 }}
              >
                Thank you for your order
              </p>
              {customerName && (
                <p 
                  className="text-2xl font-medium"
                  style={{ color: BRAND_GOLD }}
                >
                  {customerName}
                </p>
              )}
              {total > 0 && (
                <p 
                  className="text-4xl font-bold mt-4"
                  style={{ color: BRAND_GOLD }}
                >
                  ${total.toFixed(2)} paid
                </p>
              )}
            </div>

            {/* Enjoy Message */}
            <div 
              className="rounded-2xl p-6 mt-6 inline-block"
              style={{ backgroundColor: BRAND_BROWN }}
            >
              <p className="text-2xl text-white">
                Enjoy your coffee! ☕
              </p>
              <p className="text-sm text-white opacity-70 mt-2">
                See you again soon
              </p>
            </div>

            {/* Auto-refresh indicator */}
            <p className="text-sm animate-pulse" style={{ color: BRAND_BROWN, opacity: 0.5 }}>
              Returning to welcome screen...
            </p>
          </div>
        </main>

        {/* Footer */}
        <footer 
          className="py-4 px-8 text-center text-sm"
          style={{ backgroundColor: BRAND_BROWN, color: BRAND_CREAM }}
        >
          <p className="opacity-80">Bulaccino Cafe • Namaka, Nadi • bulaccinocafe.com</p>
        </footer>
      </div>
    );
  }

  // Idle Screen - Branded welcome with opening hours
  // (This now comes AFTER the special status checks)
  if (orderStatus === 'idle' || items.length === 0) {
    return (
      <div 
        className="min-h-screen flex flex-col"
        style={{ backgroundColor: BRAND_CREAM }}
        data-testid="customer-display-idle-screen"
      >
        {/* Header with Logo */}
        <header 
          className="py-6 px-8 flex items-center justify-center"
          style={{ backgroundColor: BRAND_BROWN }}
        >
          <img 
            src="/bulaccino-logo-cfd.png" 
            alt="Bulaccino Cafe" 
            className="h-24 w-auto object-contain"
          />
        </header>

        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-2xl text-center space-y-10">
            {/* Welcome Message */}
            <div className="space-y-4">
              <h1 
                className="text-5xl font-bold"
                style={{ color: BRAND_BROWN }}
              >
                Bula! Welcome
              </h1>
              <p 
                className="text-2xl animate-pulse"
                style={{ color: BRAND_BROWN, opacity: 0.7 }}
              >
                Your order will appear here
              </p>
            </div>

            {/* Opening Hours */}
            <div 
              className="rounded-2xl p-6 shadow-lg mx-auto"
              style={{ backgroundColor: 'white', maxWidth: '380px' }}
            >
              <div className="flex items-center justify-center gap-2 mb-4">
                <Clock className="w-5 h-5" style={{ color: BRAND_GOLD }} />
                <h2 
                  className="text-xl font-semibold"
                  style={{ color: BRAND_BROWN }}
                >
                  Opening Hours
                </h2>
              </div>
              <div className="space-y-1">
                {OPENING_HOURS.map(({ day, hours }) => (
                  <div 
                    key={day}
                    className={`flex justify-between py-2 px-3 rounded-lg text-sm ${
                      day === currentDay ? 'font-semibold' : ''
                    }`}
                    style={{ 
                      backgroundColor: day === currentDay ? BRAND_GOLD + '20' : 'transparent',
                      color: BRAND_BROWN
                    }}
                  >
                    <span>{day}</span>
                    <span>{hours}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer 
          className="py-4 px-8 text-center text-sm"
          style={{ backgroundColor: BRAND_BROWN, color: BRAND_CREAM }}
        >
          <p className="opacity-80">Bulaccino Cafe • Namaka, Nadi • bulaccinocafe.com</p>
        </footer>
      </div>
    );
  }

  // Active Order Screen - Branded
  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: BRAND_CREAM }}
    >
      {/* Header with Logo */}
      <header 
        className="py-4 px-6 flex items-center justify-between"
        style={{ backgroundColor: BRAND_BROWN }}
      >
        <img 
          src="/bulaccino-logo-cfd.png" 
          alt="Bulaccino Cafe" 
          className="h-14 w-auto object-contain"
        />
        {/* Order Type Badge */}
        <span 
          className="px-4 py-2 rounded-full text-lg font-semibold text-white"
          style={{ backgroundColor: orderType === 'Dine In' ? BRAND_GOLD : '#2D5F4A' }}
        >
          {orderType === 'Dine In' ? '🍽️ Dine In' : '☕ Takeaway'}
        </span>
      </header>

      {/* Items Section Label */}
      <div 
        className="px-6 py-3 flex items-center gap-2"
        style={{ backgroundColor: BRAND_BROWN + '10' }}
      >
        <ShoppingBag className="w-5 h-5" style={{ color: BRAND_BROWN }} />
        <span className="font-semibold text-lg" style={{ color: BRAND_BROWN }}>
          Your Order
        </span>
        {customerName && (
          <span className="ml-auto text-sm" style={{ color: BRAND_GOLD }}>
            {customerName}
          </span>
        )}
      </div>

      {/* Items List */}
      <main className="flex-1 p-4 overflow-auto">
        <div className="space-y-3 max-w-2xl mx-auto">
          {items.map((item, index) => (
            <div 
              key={item.id || index}
              className="bg-white rounded-xl p-4 shadow-md flex items-start justify-between animate-fadeIn"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <span 
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
                    style={{ backgroundColor: BRAND_BROWN }}
                  >
                    {item.quantity || 1}
                  </span>
                  <span 
                    className="font-semibold text-lg"
                    style={{ color: BRAND_BROWN }}
                  >
                    {item.name}
                  </span>
                </div>
                {item.modifiers && item.modifiers.length > 0 && (
                  <div className="text-sm text-gray-500 ml-11 mt-1">
                    {item.modifiers.map(m => typeof m === 'string' ? m : m.name || m.selected_option).join(', ')}
                  </div>
                )}
                {item.notes && (
                  <div className="text-sm text-amber-600 ml-11 mt-1 italic">
                    Note: {item.notes}
                  </div>
                )}
              </div>
              <span 
                className="font-semibold text-lg"
                style={{ color: BRAND_BROWN }}
              >
                {formatCurrency(item.price * (item.quantity || 1))}
              </span>
            </div>
          ))}
        </div>
      </main>

      {/* Totals Footer */}
      <footer 
        className="p-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]"
        style={{ backgroundColor: 'white' }}
      >
        <div className="max-w-2xl mx-auto space-y-2">
          {/* Subtotal */}
          <div className="flex justify-between text-base text-gray-600">
            <span>Subtotal</span>
            <span>{formatCurrency(subtotal)}</span>
          </div>

          {/* Discount if any */}
          {discount > 0 && (
            <div className="flex justify-between text-base text-green-600">
              <span>{discountName || 'Discount'}</span>
              <span>-{formatCurrency(discount)}</span>
            </div>
          )}

          {/* Tax */}
          {tax > 0 && (
            <div className="flex justify-between text-base text-gray-600">
              <span>VAT (9%)</span>
              <span>{formatCurrency(tax)}</span>
            </div>
          )}

          {/* Divider */}
          <div 
            className="border-t-2 my-2 pt-2"
            style={{ borderColor: BRAND_BROWN }}
          ></div>

          {/* Total */}
          <div className="flex justify-between items-center">
            <span 
              className="text-2xl font-bold"
              style={{ color: BRAND_BROWN }}
            >
              Total
            </span>
            <span 
              className="text-4xl font-black"
              style={{ color: BRAND_GOLD }}
            >
              {formatCurrency(total)}
            </span>
          </div>
        </div>
      </footer>

      {/* CSS for animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
